<template>
  <page-loader>
    <main>
      <v-container>
        <v-row>
          <v-col>
            <filter-accordion
              :loading="isLoading"
              :query-count="$_queryHandler_numberOfQueries"
              :query-changed="$_queryHandler_hasQueryChanged"
              @clear-filters="$_queryHandler_resetFilters"
            >
              <template #search>
                <search-input v-model="query.searchTerm" :is-loading="isLoading" clearable hide-details />
              </template>
              <template #default>
                <v-row>
                  <v-col sm="4">
                    <select-list
                      v-model="query.params.entity_type_id"
                      :is-disabled="isLoading"
                      :items="getItemsForOption('entityTypes')"
                      clearable
                      hide-details
                      label="Entity Type"
                    />
                  </v-col>
                  <v-col sm="4">
                    <select-list
                      v-model="query.params.symbol"
                      :is-disabled="isLoading"
                      :is-loading="isLoading"
                      :items="symbols"
                      clearable
                      hide-details
                      label="Symbol"
                    />
                  </v-col>
                </v-row>
              </template>
              <template #content>
                <v-list v-if="entities.length" class="py-0">
                  <entity-item
                    v-for="(entity, index) in entities"
                    :key="entity.id"
                    :entity="entity"
                    :class="{ 'grey lighten-3': index % 2 === 1 }"
                  />
                </v-list>
                <div v-else class="text-center pa-4">No results found</div>
                <v-container>
                  <v-row>
                    <v-col>
                      <v-pagination
                        v-model="pagination.currentPage"
                        :length="Math.ceil(pagination.total / pagination.perPage)"
                        :total-visible="10"
                        @input="getEntities"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </template>
            </filter-accordion>
          </v-col>
        </v-row>
      </v-container>
    </main>

    <!-- CREATE -->
    <generic-modal
      v-model="showCreateModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :is-confirm-disabled="isCreateDisabled"
      title="Create New Entity"
      confirm-text="Create Entity"
      persistent
      @modal-confirm="onCreateConfirm"
      @modal-cancel="onCreateCancel"
    >
      <autocomplete-input
        v-model="newEntity.entity_type_id"
        :disabled="isLoading"
        :items="getItemsForOption('entityTypes')"
        :rules="[v => !!v || 'Entity Type must be defined']"
        hint="Select an entity type to enable required fields"
        :persistent-hint="!newEntity.entity_type_id"
        class="mt-5"
        label="Entity Type"
      />
      <template v-if="requiredCategorisedSchemaGeo">
        <v-row v-for="(fields, category) in requiredCategorisedSchemaGeo.general" :key="category">
          <v-col class="d-flex flex-column">
            <category-options
              :category="category"
              :data="newEntity.default"
              :fields="fields"
              :is-disabled="isLoading"
              :is-loading="isLoading"
              :edit-mode="false"
              @input="onNewEntityGeoInput"
            />
            <div v-if="showDuplicateSymbolWarning" class="amber--text text--darken-4 mt-5">
              <v-icon color="warning" small>error_outline</v-icon>
              Warning: An entity with that symbol name already exists
            </div>
          </v-col>
        </v-row>
      </template>
      <template v-if="requiredCategorisedSchema">
        <v-row v-for="(fields, category) in requiredCategorisedSchema.general" :key="category">
          <v-col>
            <category-options
              :category="category"
              :data="newEntity"
              :fields="fields"
              :is-disabled="isLoading"
              :is-loading="isLoading"
              :edit-mode="false"
              @input="onNewEntityInput"
            />
          </v-col>
        </v-row>
      </template>
    </generic-modal>

    <fab-create v-if="isPermittedTo('create')" :type="'Entity'" @click="showCreateModal = true" />
  </page-loader>
</template>

<script>
// Vue
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
// Helpers
import { categoriseSchema, parseSchema } from '@/utils/parse-schema'
import isSet from '@/utils/is-set'
import { debounce } from 'lodash-es'
// Services
import { entities } from '@/services/offers'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import CategoryOptions from '@/components/category-options/'
import EntityItem from '@/views/entities/components/entity-item'
import FabCreate from '@/components/fab-create'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import FilterAccordion from '@/components/filter-accordion'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'

const blankNewEntity = () => ({ entity_type_id: undefined, required_status: false, default: { name: undefined } })

export default {
  name: 'EntitiesIndex',
  components: {
    AutocompleteInput,
    CategoryOptions,
    EntityItem,
    FabCreate,
    GenericModal,
    PageLoader,
    FilterAccordion,
    SearchInput,
    SelectList,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data() {
    return {
      entities: [],
      isLoading: false,
      isDisabled: false,
      newEntity: blankNewEntity(),
      newEntitySchema: {},
      queriesDefined: ['entity_type_id', 'searchTerm', 'symbol'],
      queryDefaults: ['entity_type_id', 'searchTerm'],
      queryTypes: { entity_type_id: 'number' },
      showCreateModal: false,
      showDuplicateSymbolWarning: false,
      symbols: [],
    }
  },
  computed: {
    ...mapState({
      entitySchemas: state => state.entities.schema,
    }),
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
      getEntityTypes: 'entityTypes/getEntityTypes',
    }),
    entityTypes() {
      return this.getEntityTypes?.data || []
    },
    isCreateDisabled() {
      if (!this.newEntityType || !Object.keys(this.newEntitySchema).length) return true

      const schema = this.newEntitySchema
      const requiredProps = schema.required
      const requiredGeoProps = schema.patternProperties.default.required

      if (requiredProps.some(model => !isSet(this.newEntity[model]))) return true
      if (requiredGeoProps.some(model => !isSet(this.newEntity.default[model]))) return true

      return false
    },
    newEntityType() {
      const entityType = this.getEntityTypeById(this.newEntity.entity_type_id)
      return entityType ? entityType.type : null
    },
    requiredCategorisedSchema() {
      if (!Object.keys(this.newEntitySchema).length) return {}
      const parsedSchema = parseSchema({ ...this.newEntitySchema.patternProperties, patternProperties: {} })
      return categoriseSchema(parsedSchema, { requiredOnly: true })
    },
    requiredCategorisedSchemaGeo() {
      if (!Object.keys(this.newEntitySchema).length) return {}
      const parsedSchemaGeo = this.newEntitySchema?.patternProperties?.default
        ? parseSchema(this.newEntitySchema.patternProperties.default)
        : {}
      return categoriseSchema(parsedSchemaGeo, { requiredOnly: true })
    },
  },
  watch: {
    async 'newEntity.entity_type_id'(id, oldId) {
      const { type } = this.getEntityTypeById(id) || {}
      const { type: oldType } = this.getEntityTypeById(oldId) || {}

      if (type === oldType) return

      this.newEntity = blankNewEntity()
      this.newEntity.entity_type_id = id

      if (type) {
        if (!this.entitySchemas[type]) {
          this.isLoading = true
          await this.getEntitySchema(type)
          this.isLoading = false
        }
        await this.$nextTick(() => {
          this.newEntitySchema = this.entitySchemas[type]
        })
      }
    },
  },
  async created() {
    await this.getEntities()
    this.getSymbols()
    this.setPageLoading(false)

    if (!this.entityTypes.length) {
      this.fetchEntityTypes({ paginate: { perPage: 999 } })
    }
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    ...mapActions({
      getEntitySchema: 'entities/getEntitySchema',
      fetchEntityTypes: 'entityTypes/fetch',
    }),
    async getSymbols() {
      const { data } = await entities.getEntities({
        perPage: null,
      })

      if (!data || !Array.isArray(data)) return []

      const symbols = data.map(
        ({
          entity: {
            default: { symbol },
          },
        }) => symbol
      )
      this.symbols = [...new Set(symbols)]
        .filter(symbol => symbol)
        .map(symbol => ({
          key: symbol,
          value: symbol,
        }))
    },
    clearFilters() {
      this.query = {
        params: { entity_type_id: undefined },
        searchTerm: undefined,
      }
    },
    async createEntity() {
      this.isLoading = true
      try {
        const {
          data: { id },
        } = await entities.createEntity(this.newEntity)
        this.$router.push({ name: 'entities.manage', params: { id } })
        this.$notify({
          type: 'success',
          text: 'Successfully created new Entity',
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to create new Entity',
        })
      }

      this.isLoading = false
      this.newEntity = blankNewEntity()
    },
    async getEntities() {
      this.isLoading = true

      try {
        const { currentPage: page, perPage } = this.pagination
        const filters = this.queryFilters

        const { data, pagination } = await entities.getEntities({
          page,
          perPage,
          filters,
          search: this.query.searchTerm,
        })
        this.entities = data
        this.pagination = pagination
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch Entities.',
        })
      }

      this.isLoading = false
    },
    getEntityTypeById(id) {
      return this.entityTypes.find(eType => eType.id === id)
    },
    handleQueryUpdate() {
      this.getEntities()
    },
    onCreateCancel() {
      this.showDuplicateSymbolWarning = false
      this.newEntity = blankNewEntity()
    },
    onCreateConfirm() {
      this.createEntity()
    },
    onNewEntityInput({ context, model, value }) {
      if (context) {
        if (this.newEntity[model]) {
          this.$set(this.newEntity[model], context, value)
        } else {
          this.$set(this.newEntity, model, { [context]: value })
        }
      } else this.$set(this.newEntity, model, value)
    },
    checkExistingSymbols: debounce(async function(symbol) {
      const { data } = await entities.getEntities({
        filters: { 'entity.default.symbol': symbol },
      })

      this.showDuplicateSymbolWarning = !!data.length
    }, 500),
    onNewEntityGeoInput({ context, model, value }) {
      if (model === 'symbol') this.checkExistingSymbols(value)

      if (context) {
        if (this.newEntity.default[model]) {
          this.$set(this.newEntity.default[model], context, value)
        } else {
          this.$set(this.newEntity.default, model, { [context]: value })
        }
      } else this.$set(this.newEntity.default, model, value)
    },
  },
}
</script>
